import { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { registeredModules } from './moduleRegistry';
import { SmartRedirect } from './SmartRedirect';
import { MODULE_CHOOSER_PATH } from './moduleLanding';
import type { ModuleRoute } from './types';
import { RequirePermission } from '@core/auth/guards/RequirePermission';
import { RequireAuth } from '@core/auth/guards/RequireAuth';
import { AppLayout } from '@shared/components/layout/AppLayout';
import { LoadingSpinner } from '@shared/components/feedback/LoadingSpinner';
import { LoginPage } from '@shared/pages/LoginPage';
import { UnauthorizedPage } from '@shared/pages/UnauthorizedPage';
import { NotFoundPage } from '@shared/pages/NotFoundPage';
import { ModuleChooserPage } from '@shared/pages/ModuleChooserPage';

const wrapRouteElement = (route: ModuleRoute) => {
  if (!route.element) {
    return null;
  }

  let element = <Suspense fallback={<LoadingSpinner />}>{route.element}</Suspense>;

  if (route.permissions && route.permissions.length > 0) {
    element = <RequirePermission permissions={route.permissions} requireAll={false}>{element}</RequirePermission>;
  } else if (route.permission) {
    element = <RequirePermission permission={route.permission}>{element}</RequirePermission>;
  }

  if (route.requireAuth !== false) {
    element = <RequireAuth>{element}</RequireAuth>;
  }

  return element;
};

const renderModuleRoutes = () =>
  registeredModules.map((module) => (
    <Route key={module.name} path={module.basePath.replace(/^\//, '')}>
      {module.routes.map((route, index) => (
        <Route
          key={`${module.name}-${index}`}
          index={route.index}
          path={route.path}
          element={wrapRouteElement(route)}
        />
      ))}
    </Route>
  ));

export const AppRouter = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/unauthorized" element={<UnauthorizedPage />} />

    <Route
      path="/"
      element={
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      }
    >
      {/* Smart redirect to first accessible module */}
      <Route index element={<SmartRedirect />} />
      {/* Dashboard also redirects to first accessible module */}
      <Route path="dashboard" element={<SmartRedirect />} />
      {/* Chooser after login when several work modules are available */}
      <Route path={MODULE_CHOOSER_PATH.slice(1)} element={<ModuleChooserPage />} />
      {renderModuleRoutes()}
    </Route>

    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);
